import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Calendar, Gauge, Fuel, Settings2, Car, ArrowUpRight } from 'lucide-react';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import './home.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  modelYear: number;
  transmission: string;
  isZeroKm: boolean;
  changed?: { featured?: boolean };
}

interface FeaturedListingCardProps {
  vehicle: Vehicle;
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);

const formatMileage = (mileage: number) => {
  if (mileage === 0) return 'Zero KM';
  return `${new Intl.NumberFormat('pt-BR').format(mileage)} km`;
};

const shortenTransmission = (value: string) => {
  if (!value?.trim()) return '—';
  return value
    .replace(/autom[aá]tico/i, 'Automático')
    .replace(/manual/i, 'Manual')
    .trim();
};

function getPromoBadge(vehicle: Vehicle): { label: string; variant: 'blue' | 'green' } | null {
  if (vehicle.changed?.featured) {
    return { label: 'Ótimo preço', variant: 'green' };
  }
  if (vehicle.isZeroKm || vehicle.mileage === 0) {
    return { label: 'Zero KM', variant: 'blue' };
  }
  if (vehicle.mileage > 0 && vehicle.mileage < 50000) {
    return { label: 'Baixa km', variant: 'blue' };
  }
  return null;
}

export function FeaturedListingCard({ vehicle }: FeaturedListingCardProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });

  const promoBadge = getPromoBadge(vehicle);
  const imageUrl = vehicle.photos?.[0];
  const optimized = imageUrl ? generateResponsiveImageUrls(imageUrl, 360, 220) : null;
  const loadingStrategy = getImageLoadingStrategy(true, false);

  useEffect(() => {
    secureGetItem('favorites').then((favorites) => {
      if (favorites && Array.isArray(favorites)) {
        setIsFavorited(favorites.includes(vehicle.id));
      }
    }).catch(() => {});

    generateVehicleSlug(vehicle).then(setVehicleSlug).catch(() => {});
  }, [vehicle]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const favorites = (await secureGetItem('favorites')) || [];
    let updated = Array.isArray(favorites) ? [...favorites] : [];
    if (updated.includes(vehicle.id)) {
      updated = updated.filter((id) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updated.push(vehicle.id);
      setIsFavorited(true);
    }
    await secureSetItem('favorites', updated);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const specs = [
    { icon: Gauge, value: formatMileage(vehicle.mileage) },
    { icon: Fuel, value: vehicle.fuel || '—' },
    { icon: Settings2, value: shortenTransmission(vehicle.transmission) },
    { icon: Calendar, value: vehicle.modelYear ? String(vehicle.modelYear) : '—' },
  ];

  return (
    <article className="wl6-featured-card">
      <Link to={`/estoque/${vehicleSlug}`} className="wl6-featured-card-link">
        <div className="wl6-featured-card-media">
          {promoBadge && (
            <span className={`wl6-featured-badge wl6-featured-badge--${promoBadge.variant}`}>
              {promoBadge.label}
            </span>
          )}

          <button
            type="button"
            className={`wl6-featured-save${isFavorited ? ' is-active' : ''}`}
            onClick={toggleFavorite}
            aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar veículo'}
          >
            <Bookmark className="h-4 w-4" fill={isFavorited ? 'currentColor' : 'none'} />
          </button>

          {optimized ? (
            <img
              src={optimized.src}
              srcSet={optimized.srcSet}
              sizes={optimized.sizes || '(max-width: 768px) 85vw, 300px'}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="wl6-featured-card-img"
              loading={loadingStrategy.loading}
              decoding="async"
              width={optimized.width}
              height={optimized.height}
            />
          ) : (
            <div className="wl6-featured-card-img wl6-featured-card-img--empty">
              <Car className="h-10 w-10 opacity-35" />
            </div>
          )}
        </div>

        <div className="wl6-featured-card-body">
          <h3 className="wl6-featured-card-title">
            {vehicle.brand}, {vehicle.model}
          </h3>
          {vehicle.version && (
            <p className="wl6-featured-card-subtitle">{vehicle.version}</p>
          )}

          <div className="wl6-featured-specs">
            {specs.map(({ icon: Icon, value }, index) => (
              <div key={index} className="wl6-featured-spec">
                <Icon className="wl6-featured-spec-icon" />
                <span>{value}</span>
              </div>
            ))}
          </div>

          <div className="wl6-featured-card-footer">
            <span className="wl6-featured-price">{formatPrice(vehicle.price)}</span>
            <span className="wl6-featured-cta">
              Ver detalhes
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
